import type { AccountSettingsResponse } from '../../models/common.model';
import { modalService } from '../../modal/modal.service';
import { store } from '../../store';
import { LegacyAuthModal } from '../components/legacy-auth-modal/legacy-auth-modal.component';
import type { AuthenticationRequest, AuthenticationResult } from '../models/auth.model';
import { AccountType, LoginType } from '../models/auth.model';
import { authenticationResult } from '../store/auth.reducer';
import {
  selectAccountSettings,
  selectAccountSettingsLoaded,
  selectAccountType,
  selectAuthentication,
  selectIsAuthenticated,
} from '../store/auth.selectors';
import { checkAccountType, checkAuthentication, loadAccountSettings } from '../store/auth.thunk';

class AuthenticationFlowService {
  // A second trigger while the auth checks are still resolving (a double tap
  // on Log in, a click racing the proxy-page bounce) must not run the flow
  // twice: on legacy-accounts stores that stacked two auth modals (SNGL-7251).
  private inProgress = false;

  /**
   * Initiate authentication flow
   * Checks current auth status and account type, then routes to appropriate method
   */
  async initiateAuthentication(request: AuthenticationRequest): Promise<void> {
    // The checks stay outside the guard so a retry can still recover a
    // failed check; only the wait-and-route runs once.
    store.dispatch(checkAuthentication({ loginTypes: [LoginType.CUSTOMER] }));
    store.dispatch(checkAccountType());
    if (!selectAccountSettingsLoaded(store.getState())) {
      store.dispatch(loadAccountSettings());
    }

    if (this.inProgress) {
      return;
    }
    this.inProgress = true;

    try {
      const result = await this.waitForAuthChecks();

      this.processAuthentication(
        result.authentication,
        result.accountType,
        result.accountSettingsResponse,
        request
      );
    } finally {
      this.inProgress = false;
    }
  }

  private async waitForAuthChecks(): Promise<{
    authentication: any;
    accountType: AccountType;
    accountSettingsResponse: AccountSettingsResponse;
  }> {
    return new Promise((resolve) => {
      const checkState = () => {
        const state = store.getState();
        const authentication = selectAuthentication(state);
        const accountType = selectAccountType(state);
        const accountSettingsLoaded = selectAccountSettingsLoaded(state);

        if (authentication && accountType && accountSettingsLoaded) {
          resolve({
            authentication,
            accountType,
            accountSettingsResponse: selectAccountSettings(state),
          });
        } else {
          setTimeout(checkState, 50);
        }
      };

      checkState();
    });
  }

  private processAuthentication(
    authResponse: any,
    accountType: AccountType,
    accountSettingsResponse: AccountSettingsResponse,
    request: AuthenticationRequest
  ): void {
    const accountSettings = accountSettingsResponse.accountSettings;
    if (authResponse.authenticated) {
      this.processAuthenticated(request);
    } else if (this.hasCustomLoginUrl(accountSettings)) {
      this.processCustomLoginUrl(request, accountSettings);
    } else if (accountType === AccountType.NEW) {
      this.processNewUnauthenticated(request);
    } else {
      this.processLegacyUnauthenticated(request);
    }
  }

  private hasCustomLoginUrl(accountSettings: AccountSettingsResponse['accountSettings']): boolean {
    const url = accountSettings?.productFormLoginUrl;
    return !!url && url.trim() !== '';
  }

  private processCustomLoginUrl(
    request: AuthenticationRequest,
    accountSettings: AccountSettingsResponse['accountSettings']
  ): void {
    const redirectUrl = new URL(
      request.returnToUrl && !request.preventLoad ? request.returnToUrl : window.location.href
    );
    const relativeUrl = redirectUrl.pathname + redirectUrl.search;

    if (!request.preventLoad) {
      const destination = redirectUrl.pathname + redirectUrl.search + redirectUrl.hash;
      localStorage.setItem('authReturnTo', destination);
    }

    let loadUrl = accountSettings.productFormLoginUrl;
    const paramName = accountSettings.productFormLoginRedirectParameter;
    if (paramName && paramName.trim() !== '') {
      loadUrl = loadUrl + '?' + paramName + '=' + encodeURIComponent(relativeUrl);
    }

    store.dispatch(
      authenticationResult({
        success: true,
        termsAccepted: false,
        optInToMarketingEmails: !this.hasTerms(request),
        loadUrl,
      })
    );
  }

  /**
   * Handle already-authenticated users
   */
  private processAuthenticated(request: AuthenticationRequest): void {
    const result = {
      success: true,
      termsAccepted: false,
      loadUrl:
        request.returnToUrl && !request.preventLoad
          ? request.returnToUrl
          : request.preventLoad
            ? undefined
            : window.location.href,
      optInToMarketingEmails: !this.hasTerms(request),
    };
    store.dispatch(authenticationResult(result));
  }

  /**
   * Handle NEW account type (redirect to external Shopify login)
   */
  private processNewUnauthenticated(request: AuthenticationRequest): void {
    const redirectUrl = new URL(
      request.returnToUrl && !request.preventLoad ? request.returnToUrl : window.location.href
    );
    const relativeUrl = encodeURIComponent(redirectUrl.pathname + redirectUrl.search);

    // Store the full intended destination including the hash fragment.
    // Server-side return_to cannot preserve the hash (it's never sent to the server),
    // so we handle the final navigation ourselves in processStoredAuthenticationResult.
    if (!request.preventLoad) {
      const destination = redirectUrl.pathname + redirectUrl.search + redirectUrl.hash;
      localStorage.setItem('authReturnTo', destination);
    }

    store.dispatch(
      authenticationResult({
        success: true,
        termsAccepted: false,
        optInToMarketingEmails: !this.hasTerms(request),
        loadUrl: `/customer_authentication/login?return_to=${relativeUrl}`,
      })
    );
  }

  /**
   * Handle LEGACY account type (open authentication modal)
   *
   * Note: This requires implementing a modal component in Preact
   * See example modal implementation below
   */
  private processLegacyUnauthenticated(request: AuthenticationRequest): void {
    modalService.open({
      component: LegacyAuthModal,
      props: {
        state: request.state,
        allowedStates: request.allowedStates,
        terms: request.terms,
        onClose: (result: AuthenticationResult) => this.handleLegacyAuthResult(result, request),
      },
    });
  }

  /**
   * Handle authentication result from modal
   */
  private handleLegacyAuthResult(
    result: AuthenticationResult,
    request: AuthenticationRequest
  ): void {
    modalService.closeAll();
    if (result.success && !request.preventLoad) {
      result.loadUrl = request.returnToUrl ?? window.location.href;
    }
    result.rejectUrl = request.rejectUrl;

    store.dispatch(authenticationResult(result));
  }

  /**
   * Process stored authentication result from localStorage
   * Called after returning from external authentication redirect
   */
  processStoredAuthenticationResult(): void {
    const resultString = localStorage.getItem('authenticationResult');
    if (resultString && resultString !== '') {
      localStorage.removeItem('authenticationResult');

      // The result and authReturnTo are stored BEFORE navigating away to the
      // login page, so their presence doesn't mean the login completed. If the
      // visitor came back unauthenticated (abandoned login, back button),
      // replaying authReturnTo would teleport them to the page that triggered
      // the login redirect — and straight back to login. Discard instead;
      // every caller runs after checkAuthentication has resolved.
      if (!selectIsAuthenticated(store.getState())) {
        localStorage.removeItem('authReturnTo');
        return;
      }

      try {
        const result = JSON.parse(resultString) as AuthenticationResult;

        // Navigate to the stored return destination if present.
        // For new-account logins we stored the full URL (including hash) in
        // authReturnTo before redirecting to Shopify, because server-side
        // return_to strips the hash fragment used by hash-based routing.
        const returnTo = localStorage.getItem('authReturnTo');
        if (returnTo) {
          localStorage.removeItem('authReturnTo');
          const currentPath = window.location.pathname + window.location.search + window.location.hash;
          result.loadUrl = currentPath !== returnTo ? returnTo : undefined;
        } else {
          result.loadUrl = undefined;
        }

        store.dispatch(authenticationResult(result));
      } catch (error) {
        console.error('Failed to parse stored authentication result:', error);
      }
    }
  }

  /**
   * Store authentication result in localStorage
   * Used before redirecting to external authentication
   */
  storeAuthenticationResult(result: AuthenticationResult): void {
    localStorage.setItem('authenticationResult', JSON.stringify(result));
  }

  /**
   * Check if request has terms that need acceptance
   */
  private hasTerms(request: AuthenticationRequest): boolean {
    return !!request.terms && request.terms.trim() !== '';
  }
}

export const authenticationFlowService = new AuthenticationFlowService();
